import React from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from '@heroui/react';
import { toast } from 'react-toastify';

import type { ExperimentItem } from '@/types';
import { useAtom } from 'jotai';
import { setExperimentStatus } from '@/store';

const MAX_REASON_LENGTH = 2048;

interface BlockExperimentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: ExperimentItem;
  onSuccess?: () => void;
}

export function BlockExperimentModal({
  isOpen,
  onOpenChange,
  experiment,
  onSuccess
}: BlockExperimentModalProps) {
  const [blockingReason, setBlockingReason] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, setStatusAtom] = useAtom(setExperimentStatus);

  React.useEffect(() => {
    if (isOpen) {
      setBlockingReason('');
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(async () => {
    const trimmed = blockingReason.trim();
    if (!trimmed) {
      toast.error('Please provide a reason for blocking this experiment');
      return;
    }
    try {
      setIsSubmitting(true);
      await setStatusAtom({
        id: experiment.id,
        status: 'blocked',
        blocking_reason: trimmed
      });
      toast.success('Experiment blocked successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to block experiment:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [blockingReason, experiment.id, setStatusAtom, onOpenChange, onSuccess]);

  const handleClose = React.useCallback(() => {
    setBlockingReason('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Block Experiment</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <p className="text-default-600 text-sm">
                  You are about to block <span className="font-semibold">{experiment.name}</span>. This will
                  immediately stop the experiment from running. The reason you provide will be recorded for audit
                  purposes.
                </p>
                <Textarea
                  label="Reason for blocking (required)"
                  placeholder="e.g., Policy violation, malicious content, terms of service breach..."
                  value={blockingReason}
                  onValueChange={setBlockingReason}
                  variant="bordered"
                  minRows={3}
                  maxLength={MAX_REASON_LENGTH}
                  description={`${blockingReason.length}/${MAX_REASON_LENGTH} characters`}
                  isInvalid={blockingReason.trim().length === 0}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                color="danger"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={!blockingReason.trim()}
              >
                Block Experiment
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
